import React from "react";
import { CheckCircle, Save, ChevronDown, ChevronUp } from "lucide-react";
import {
  useTrackEvents,
  useScriptTrackEvents,
  useUpdateScriptTrackEvents,
} from "../../hooks/useTracks";

interface Props {
  scriptId: number;
  readOnly?: boolean;
}

const FlowTrackEventsSection: React.FC<Props> = ({ scriptId, readOnly }) => {
  const { data: all, isLoading: loadingAll } = useTrackEvents({
    page_size: 200,
  });
  const { data: assigned, isLoading: loadingAssigned } =
    useScriptTrackEvents(scriptId);
  const assignedIds = new Set(
    (assigned?.track_events || []).map((te) => te.id),
  );
  const [selected, setSelected] = React.useState<number[]>([]);
  const updateMutation = useUpdateScriptTrackEvents(scriptId);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setSelected(Array.from(assignedIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned?.track_events?.length]);

  const toggle = (id: number) => {
    if (readOnly) return;
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const onSave = async () => {
    await updateMutation.mutateAsync(selected);
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Track events</h2>
          <p className="text-sm text-gray-500">
            Select which global track events this flow should request from the
            frontend.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium bg-white hover:bg-gray-50"
          >
            {open ? (
              <ChevronUp className="h-4 w-4 mr-1" />
            ) : (
              <ChevronDown className="h-4 w-4 mr-1" />
            )}
            {open ? "Hide" : "Show"}
          </button>
          {!readOnly && (
            <button
              onClick={onSave}
              disabled={updateMutation.isPending}
              className="inline-flex items-center px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="p-4">
          {loadingAll || loadingAssigned ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(all?.items || []).map((item) => {
                const isSel = selected.includes(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => toggle(item.id)}
                    className={`flex items-start justify-between text-left w-full px-3 py-2 border rounded-md ${isSel ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white"} ${readOnly ? "cursor-not-allowed opacity-60" : "hover:bg-gray-50"}`}
                    disabled={readOnly}
                  >
                    <div>
                      <div className="font-medium text-gray-900">
                        {item.name}
                      </div>
                      {item.description ? (
                        <div className="text-sm text-gray-600">
                          {item.description}
                        </div>
                      ) : null}
                      {item.transform ? (
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="text-gray-500">Transform:</span>{" "}
                          <code className="px-1 py-0.5 bg-gray-100 rounded">
                            {item.transform}
                          </code>
                        </div>
                      ) : null}
                    </div>
                    {isSel ? (
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                    ) : (
                      <span className="h-5 w-5 rounded-full border border-gray-300" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FlowTrackEventsSection;
